import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Edit2, X } from 'lucide-react';

interface PayrollOverrideCellProps {
  /** The current displayed value (with override applied if any) */
  value: number;
  /** The auto-calculated value (without override) */
  autoValue: number;
  /** Whether this field has been manually overridden */
  isOverridden: boolean;
  /** Display format: 'number' for plain numbers, 'minutes' for HH:MM, 'currency' for yen */
  format: 'number' | 'minutes' | 'currency';
  /** Called when user edits the value. Pass null to clear override. */
  onChange: (newValue: number | null) => void;
  /** Whether the cell is editable at all */
  editable?: boolean;
}

const formatValue = (value: number, format: 'number' | 'minutes' | 'currency'): string => {
  if (format === 'minutes') {
    const h = Math.floor(value / 60);
    const m = value % 60;
    return `${h}:${String(m).padStart(2, '0')}`;
  }
  if (format === 'currency') {
    return value.toLocaleString('ja-JP');
  }
  return String(value);
};

const parseInputValue = (input: string, format: 'number' | 'minutes' | 'currency'): number | null => {
  if (format === 'minutes') {
    // Accept HH:MM or just minutes
    if (input.includes(':')) {
      const [h, m] = input.split(':').map(Number);
      if (Number.isFinite(h) && Number.isFinite(m)) return h * 60 + m;
      return null;
    }
    const n = Number(input);
    return Number.isFinite(n) ? n : null;
  }
  const n = Number(input.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
};

const PayrollOverrideCell: React.FC<PayrollOverrideCellProps> = ({
  value,
  autoValue,
  isOverridden,
  format,
  onChange,
  editable = true,
}) => {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const startEdit = useCallback(() => {
    if (!editable) return;
    setInputValue(String(value));
    setEditing(true);
  }, [editable, value]);

  const commitEdit = useCallback(() => {
    setEditing(false);
    const parsed = parseInputValue(inputValue, format);
    if (parsed === null) return;
    if (parsed === autoValue) {
      // Same as auto value → remove override
      onChange(null);
    } else {
      onChange(parsed);
    }
  }, [inputValue, format, autoValue, onChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      commitEdit();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  }, [commitEdit]);

  const clearOverride = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  }, [onChange]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onBlur={commitEdit}
        onKeyDown={handleKeyDown}
        className="w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-right"
      />
    );
  }

  const displayValue = formatValue(value, format);

  if (!editable) {
    return (
      <span className="text-sm text-gray-900 text-right block px-2 py-1">
        {displayValue}
      </span>
    );
  }

  return (
    <div
      onClick={startEdit}
      className={`group relative cursor-pointer px-2 py-1 rounded text-sm text-right ${
        isOverridden
          ? 'bg-amber-50 text-amber-900 border border-amber-200'
          : 'hover:bg-gray-100 text-gray-900'
      }`}
      title={isOverridden ? `自動計算値: ${formatValue(autoValue, format)}` : undefined}
    >
      <span>{displayValue}</span>
      {isOverridden && (
        <button
          onClick={clearOverride}
          className="absolute -top-1 -right-1 w-4 h-4 bg-amber-200 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
          title="上書きを解除"
        >
          <X className="w-3 h-3 text-amber-700" />
        </button>
      )}
      {!isOverridden && editable && (
        <Edit2 className="inline-block w-3 h-3 ml-1 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )}
    </div>
  );
};

export default PayrollOverrideCell;
